/**
 * HTTP client for the Jeba API.
 *
 * Owns three things the rest of the app should not have to think about:
 *
 *   1. Auth headers, read from the token store.
 *   2. Transparent token refresh on a 401 — access tokens live 24h, so a booth
 *      or console left open overnight would otherwise die mid-session.
 *   3. Typed errors, so callers can branch on quota exhaustion or rate limiting
 *      rather than string-matching a message.
 */

import { tokenStore } from "./tokens";
import { DEMO, demoRequest, DemoNotFound, DemoUnsupported } from "./demo";

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly details?: unknown;

  constructor(status: number, message: string, code?: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }

  get isUnauthorized() { return this.status === 401; }
  get isForbidden() { return this.status === 403; }
  get isNotFound() { return this.status === 404; }
  get isQuotaExceeded() { return this.status === 402 || this.code === "quota_exceeded"; }
  get isRateLimited() { return this.status === 429; }
  get isValidation() { return this.status === 422; }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Skip the Authorization header — used by login and public booth reads. */
  anonymous?: boolean;
  /** Internal: prevents an infinite refresh loop. */
  _retried?: boolean;
  raw?: boolean;
}

function url(path: string): string {
  return path.startsWith("http") ? path : `${BASE_URL}${path}`;
}

async function parseError(response: Response): Promise<ApiError> {
  let message = response.statusText || `Request failed (${response.status})`;
  let code: string | undefined;
  let details: unknown;

  try {
    const payload = await response.json();
    // Rails renders errors as {error}, {errors}, or {error: [..]} depending on
    // whether the failure came from a controller, a model, or dry-schema.
    if (typeof payload?.error === "string") message = payload.error;
    else if (Array.isArray(payload?.error)) message = payload.error.join(", ");
    else if (payload?.errors) {
      details = payload.errors;
      message = summariseErrors(payload.errors);
    }
    code = payload?.code;
  } catch {
    /* Non-JSON body (HTML error page, empty 500) — keep the status text. */
  }

  return new ApiError(response.status, message, code, details);
}

function summariseErrors(errors: unknown): string {
  if (Array.isArray(errors)) return errors.join(", ");
  if (errors && typeof errors === "object") {
    const parts = Object.entries(errors as Record<string, unknown>).map(
      ([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : String(value)}`,
    );
    return parts.join(" · ") || "Validation failed";
  }
  return String(errors ?? "Validation failed");
}

/** Serialised so concurrent 401s trigger exactly one refresh, not N. */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  const refreshToken = tokenStore.refreshToken;
  if (!refreshToken) return false;

  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(url("/api/auth/refresh"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!response.ok) {
        tokenStore.clear();
        return false;
      }
      const session = await response.json();
      tokenStore.set(session.token, session.refresh_token);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, anonymous, _retried, raw, headers, ...rest } = options;

  // Demo mode answers from an in-memory store so the app runs with no backend.
  // Anything the store doesn't recognise falls through to a real request.
  if (DEMO) {
    try {
      const mocked = await demoRequest(path, rest.method ?? "GET", body);
      if (mocked !== undefined) return mocked as T;
    } catch (error) {
      if (error instanceof DemoNotFound) throw new ApiError(404, error.message);
      if (error instanceof DemoUnsupported) throw new ApiError(501, error.message);
      throw error;
    }
  }

  const isFormData = body instanceof FormData;
  const finalHeaders = new Headers(headers);

  if (!isFormData && body !== undefined) finalHeaders.set("Content-Type", "application/json");
  if (!anonymous && tokenStore.accessToken) {
    finalHeaders.set("Authorization", `Bearer ${tokenStore.accessToken}`);
  }
  if (tokenStore.organizationId) {
    // Superadmins act on behalf of another tenant. Ignored for everyone else.
    finalHeaders.set("X-Organization-Id", String(tokenStore.organizationId));
  }

  const response = await fetch(url(path), {
    ...rest,
    headers: finalHeaders,
    body: isFormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (response.status === 401 && !anonymous && !_retried && tokenStore.refreshToken) {
    if (await refreshSession()) {
      return request<T>(path, { ...options, _retried: true });
    }
    tokenStore.clear();
  }

  if (!response.ok) throw await parseError(response);

  if (raw) return response as unknown as T;
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "POST", body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "PATCH", body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "PUT", body }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: "DELETE" }),
};

/** Downloads a binary response (YAML export, generation ZIP) as a file. */
export async function download(path: string, filename: string, options: RequestOptions = {}): Promise<void> {
  const response = await request<Response>(path, { ...options, raw: true });
  const blob = await response.blob();
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(href);
}

export { BASE_URL };
